import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getOrder, type Order } from '../../../utils/firebase';
import { ArrowLeft, Package, Mail, MapPin, CreditCard } from 'lucide-react';
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}
export function AdminOrderDetail() {
  const {
    orderId
  } = useParams<{
    orderId: string;
  }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!orderId) return;
    getOrder(orderId).then(data => {
      setOrder(data);
      setLoading(false);
    });
  }, [orderId]);
  if (loading) {
    return <div className="p-8 flex justify-center" data-id="element-291" data-mp="3mu1z0xj9a1b3">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-292" data-mp="3msqx739df3mm" />
      </div>;
  }
  if (!order) {
    return <div className="p-8 text-center" data-id="element-293" data-mp="2tkip699cbqao">
        <p className="text-[#5A5A5A]" data-id="element-294" data-mp="2tlkcpd7jm59a">Order not found.</p>
        <Link to="/admin/orders" className="text-tealslate hover:underline mt-4 inline-block" data-id="element-295" data-mp="2otaezb06s0wg">
          Back to Orders
        </Link>
      </div>;
  }
  const hasPhysical = order.lineItems.some(i => i.type === 'supplement');
  const hasDigital = order.lineItems.some(i => i.type === 'book');
  return <div className="p-8 max-w-4xl mx-auto" data-id="element-296" data-mp="2asznq6uyskar">
      <Link to="/admin/orders" className="inline-flex items-center gap-2 text-sm font-medium text-[#5A5A5A] hover:text-[#111111] mb-6 transition-colors" data-id="element-297" data-mp="25uy9bz8hh95m">
        <ArrowLeft className="w-4 h-4" data-id="element-298" data-mp="20yg5d1kz9euc" />
        Back to Orders
      </Link>

      <div className="flex items-center justify-between mb-8" data-id="element-299" data-mp="431v2huxll75">
        <div data-id="element-300" data-mp="3vb5z9jbcio1q">
          <h1 className="text-2xl font-bold text-[#111111] mb-1" data-id="element-301" data-mp="3vbfq1947sz8h">
            Order #{order.id.slice(0, 8).toUpperCase()}
          </h1>
          <p className="text-sm text-[#5A5A5A]" data-id="element-302" data-mp="1tj43sxtlml9v">
            {formatDate(order.createdAt)}
          </p>
        </div>
        <div className="flex gap-2" data-id="element-303" data-mp="1jn13lxib4wpk">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider
            ${order.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-304" data-mp="1jo3tq37t2v4f">
            {order.status}
          </span>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider
            ${order.fulfillmentStatus === 'pending' ? 'bg-amber-100 text-amber-800' : order.fulfillmentStatus === 'shipped' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-305" data-mp="2h9hyyx7hd08o">
            {order.fulfillmentStatus.replace('_', ' ')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-306" data-mp="vyhy1pqn6y16">
        {/* Customer Info */}
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-sm" data-id="element-307" data-mp="3a34q4sjfc6o9">
          <div className="flex items-center gap-2 mb-4 text-[#111111]" data-id="element-308" data-mp="35539ee9h0jfy">
            <Mail className="w-5 h-5 text-[#9A9A90]" data-id="element-309" data-mp="3j5xhevslwie3" />
            <h2 className="font-bold" data-id="element-310" data-mp="3j3jl6jm6x04y">Customer</h2>
          </div>
          <p className="text-sm font-medium text-[#111111] mb-1" data-id="element-311" data-mp="3f5dkntby3gsh">
            {order.shippingAddress?.recipientName || 'Digital Customer'}
          </p>
          <p className="text-sm text-[#5A5A5A] break-all" data-id="element-312" data-mp="2mfn4zfuo06u9">{order.email}</p>
        </div>

        {/* Shipping Info */}
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-sm" data-id="element-313" data-mp="3071odnshia1u">
          <div className="flex items-center gap-2 mb-4 text-[#111111]" data-id="element-314" data-mp="2vgbj0ak26775">
            <MapPin className="w-5 h-5 text-[#9A9A90]" data-id="element-315" data-mp="2ve8jmzx0n1pr" />
            <h2 className="font-bold" data-id="element-316" data-mp="27pfmx18kkpu3">Shipping</h2>
          </div>
          {!hasPhysical ? <p className="text-sm text-[#5A5A5A] italic" data-id="element-317" data-mp="23nudjpv8uton">
              Digital Product - No shipping required.
            </p> : order.shippingAddress ? <div className="text-sm text-[#5A5A5A] space-y-1" data-id="element-318" data-mp="2lcy2xmilfj1q">
              <p className="font-medium text-[#111111]" data-id="element-319" data-mp="2ldhemlhnhb7b">
                {order.shippingAddress.recipientName}
              </p>
              <p data-id="element-320" data-mp="2v30xhr5d5526">{order.shippingAddress.line1}</p>
              {order.shippingAddress.line2 && <p data-id="element-321" data-mp="302dsx0aavkgd">{order.shippingAddress.line2}</p>}
              <p data-id="element-322" data-mp="301u6p7dma9wj">
                {order.shippingAddress.city}, {order.shippingAddress.state}{' '}
                {order.shippingAddress.postalCode}
              </p>
              <p data-id="element-323" data-mp="393tt6f7ei66v">{order.shippingAddress.country}</p>
              {order.trackingNumber && <p className="mt-3 pt-3 border-t border-[#E5E5E5]" data-id="element-324" data-mp="3e0txakkowyvc">
                  <span className="font-medium text-[#111111]" data-id="element-325" data-mp="1ep0wecus79dc">Tracking:</span>{' '}
                  {order.trackingNumber}
                </p>}
            </div> : <p className="text-sm text-[#5A5A5A]" data-id="element-326" data-mp="1c4cpyqsww8gf">
              No shipping address provided.
            </p>}
        </div>

        {/* Payment Info */}
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-sm" data-id="element-327" data-mp="17cbgfcn4ovu3">
          <div className="flex items-center gap-2 mb-4 text-[#111111]" data-id="element-328" data-mp="12fb7mtyw9eza">
            <CreditCard className="w-5 h-5 text-[#9A9A90]" data-id="element-329" data-mp="xg84xg6dzkuh" />
            <h2 className="font-bold" data-id="element-330" data-mp="xdlyxkz59ded">Payment</h2>
          </div>
          <p className="text-sm text-[#5A5A5A] mb-1" data-id="element-331" data-mp="1v2ozidnqjkcj">
            Status:{' '}
            <span className="font-medium text-[#111111] capitalize" data-id="element-332" data-mp="1q55mti6orfmp">
              {order.status}
            </span>
          </p>
          <p className="text-sm text-[#5A5A5A] break-all" data-id="element-333" data-mp="1l36ia4z7mco6">
            Session: {order.stripeSessionId}
          </p>
        </div>
      </div>

      {/* Line Items */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm overflow-hidden mb-8" data-id="element-334" data-mp="9qxb6ingeqpu">
        <div className="p-4 border-b border-[#E5E5E5] bg-[#FAFAFA]" data-id="element-335" data-mp="4pq2mhdhlni4">
          <h2 className="font-bold text-[#111111]" data-id="element-336" data-mp="2swzq9j1z4fxy">Products Ordered</h2>
        </div>
        <table className="w-full text-left border-collapse" data-id="element-337" data-mp="2suvkswoff8vk">
          <thead data-id="element-338" data-mp="2wxs0ofml0qvt">
            <tr className="border-b border-[#E5E5E5]" data-id="element-339" data-mp="2ww7gnjxw8qc7">
              <th className="px-6 py-3 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-340" data-mp="2izbx2xk0nghw">
                Item
              </th>
              <th className="px-6 py-3 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider text-right" data-id="element-341" data-mp="2nvsl7rr7tuuz">
                Price
              </th>
              <th className="px-6 py-3 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider text-right" data-id="element-342" data-mp="2dzphihgpto5x">
                Qty
              </th>
              <th className="px-6 py-3 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider text-right" data-id="element-343" data-mp="205y6f4nc5w83">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E5]" data-id="element-344" data-mp="1qaenrjxs8u1a">
            {order.lineItems.map((item, i) => <tr key={i} data-id="element-345" data-mp="1v86dq8eva62u" data-mpi={i}>
                <td className="px-6 py-4" data-id="element-346" data-mp="1bn68a6xfy2ji">
                  <p className="text-sm font-medium text-[#111111]" data-id="element-347" data-mp="1bo7muldo175z">
                    {item.name}
                  </p>
                  <p className="text-xs text-[#5A5A5A] capitalize" data-id="element-348" data-mp="17ndwllcr3g9x">
                    {item.type}
                  </p>
                </td>
                <td className="px-6 py-4 text-sm text-[#5A5A5A] text-right" data-id="element-349" data-mp="1gmirmhp3srhc">
                  {formatPrice(item.unitPrice)}
                </td>
                <td className="px-6 py-4 text-sm text-[#5A5A5A] text-right" data-id="element-350" data-mp="1ukh5llziilwb">
                  {item.quantity}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-[#111111] text-right" data-id="element-351" data-mp="1qim4oilhuaj2">
                  {formatPrice(item.unitPrice * item.quantity)}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Order Summary */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm p-6 ml-auto w-full md:w-80" data-id="element-352" data-mp="2ea9cvjaz6ww0">
        <h2 className="font-bold text-[#111111] mb-4" data-id="element-353" data-mp="298tszukjawf7">Order Summary</h2>
        <div className="space-y-3 text-sm" data-id="element-354" data-mp="txstih1r3xid">
          <div className="flex justify-between text-[#5A5A5A]" data-id="element-355" data-mp="yxowqe2mjkwv">
            <span data-id="element-356" data-mp="yw42kpk823zj">Subtotal</span>
            <span data-id="element-357" data-mp="13ws3c2y95z6y">{formatPrice(order.subtotal)}</span>
          </div>
          {hasPhysical && <div className="flex justify-between text-[#5A5A5A]" data-id="element-358" data-mp="18vuxgscp2l05">
              <span data-id="element-359" data-mp="18vcvdf3cpckb">Shipping</span>
              <span data-id="element-360" data-mp="b84e5wadupo4">{formatPrice(order.total - order.subtotal)}</span>{' '}
              {/* Simplified for prototype */}
            </div>}
          <div className="pt-3 border-t border-[#E5E5E5] flex justify-between font-bold text-[#111111] text-base" data-id="element-361" data-mp="g7z5g0y2lh9p">
            <span data-id="element-362" data-mp="l4z9657g67xn">Grand Total</span>
            <span data-id="element-363" data-mp="px0knowsjumm">{formatPrice(order.total)}</span>
          </div>
        </div>
      </div>
    </div>;
}